/* global debounce, Swiper */

if (!customElements.get('product-zoom-modal')) {
  customElements.define('product-zoom-modal', class ProductZoomModal extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.isDesktop = null;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
      this.handleFocusTrap = this.handleFocusTrap.bind(this);
      this.handleResize = debounce(this.onResize.bind(this), 250);
    }

    connectedCallback() {
      this.closeButton = this.querySelector('[data-zoom-close]');
      this.counterElement = this.querySelector('[data-zoom-counter]');
      this.thumbnailsContainer = this.querySelector('[data-zoom-thumbs]');
      this.mainContainer = this.querySelector('[data-zoom-main]');

      this.closeButton?.addEventListener('click', () => this.hide());
      this.addEventListener('click', this.handleBackdropClick);
      this.bindThumbnailClicks();
    }

    bindThumbnailClicks() {
      const buttons = this.querySelectorAll('[data-slide-index]');
      for (const button of buttons) {
        button.addEventListener('click', this.handleThumbnailClick);
      }
    }

    disconnectedCallback() {
      this.destroySwipers();
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keydown', this.handleFocusTrap);
      window.removeEventListener('resize', this.handleResize);
    }

    handleBackdropClick(event) {
      if (event.target === this) {
        this.hide();
      }
    }

    handleThumbnailClick(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const button = event.currentTarget;
      if (!this.mainSwiper) return;

      const slideIndex = Number.parseInt(button.dataset.slideIndex, 10);
      if (Number.isNaN(slideIndex)) return;

      const currentIndex = this.mainSwiper.activeIndex;
      if (currentIndex === slideIndex) return;

      this.pauseSlideMedia(currentIndex);
      this.mainSwiper.slideTo(slideIndex);
    }

    updateActiveThumbnail(activeIndex) {
      if (!this.thumbnailsContainer) return;

      const thumbnails = this.thumbnailsContainer.querySelectorAll('[data-slide-index]');
      for (const [index, thumb] of [...thumbnails].entries()) {
        const isActive = index === activeIndex;
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        thumb.classList.toggle('swiper-slide-thumb-active', isActive);
      }

      if (this.thumbsSwiper) {
        this.thumbsSwiper.slideTo(activeIndex);
      }
    }

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.hide();
        return;
      }
      if (event.key === 'ArrowLeft') {
        this.mainSwiper?.slidePrev();
        this.focusActiveThumbnail();
        return;
      }
      if (event.key === 'ArrowRight') {
        this.mainSwiper?.slideNext();
        this.focusActiveThumbnail();
      }
    }

    handleFocusTrap(event) {
      if (event.key !== 'Tab') return;

      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;

      const [first] = focusableElements;
      const last = focusableElements.slice().reverse().find(() => true);
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first?.focus();
      } else if (!focusableElements.includes(activeElement)) {
        event.preventDefault();
        first?.focus();
      }
    }

    getFocusableElements() {
      const elements = [];
      
      if (this.closeButton) {
        elements.push(this.closeButton);
      }

      const thumbnails = this.thumbnailsContainer?.querySelectorAll('[data-slide-index]');
      if (thumbnails) {
        for (const thumbnail of thumbnails) {
          elements.push(thumbnail);
        }
      }

      const activeSlide = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
      if (activeSlide) {
        const videoElements = activeSlide.querySelectorAll('video, iframe');
        for (const el of videoElements) {
          elements.push(el);
        }
      }

      return elements;
    }

    updateSlideFocusability() {
      if (!this.mainContainer) return;

      const slides = this.mainContainer.querySelectorAll('.swiper-slide');
      const activeIndex = this.mainSwiper?.activeIndex ?? 0;

      slides.forEach((slide, index) => {
        const focusableMedia = slide.querySelectorAll('video, iframe');
        const isActive = index === activeIndex;

        for (const el of focusableMedia) {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      });
    }

    focusActiveThumbnail() {
      if (!this.mainSwiper || !this.thumbnailsContainer) return;
      
      const activeIndex = this.mainSwiper.activeIndex;
      const thumbnail = this.thumbnailsContainer.querySelector(`[data-slide-index="${activeIndex}"]`);
      thumbnail?.focus();
    }

    onResize() {
      const isDesktopNow = window.matchMedia('(min-width: 750px)').matches;
      
      if (this.isDesktop !== isDesktopNow) {
        this.isDesktop = isDesktopNow;
        const currentIndex = this.mainSwiper?.activeIndex ?? 0;
        this.destroySwipers();
        this.initSwipers(currentIndex);
      }
    }

    show(initialIndex = 0) {
      this.setAttribute('open', '');
      document.documentElement.classList.add('product-zoom-modal-open');
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('keydown', this.handleFocusTrap);
      window.addEventListener('resize', this.handleResize);
      
      this.isDesktop = window.matchMedia('(min-width: 750px)').matches;
      this.initSwipers(initialIndex);
      this.updateCounter();
      this.updateSlideFocusability();
      this.updateActiveThumbnail(initialIndex);
      
      const initialThumbnail = this.thumbnailsContainer?.querySelector(`[data-slide-index="${initialIndex}"]`);
      if (initialThumbnail) {
        initialThumbnail.focus();
      } else {
        this.closeButton?.focus();
      }
    }

    hide() {
      this.pauseAllMedia();
      
      this.removeAttribute('open');
      document.documentElement.classList.remove('product-zoom-modal-open');
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keydown', this.handleFocusTrap);
      window.removeEventListener('resize', this.handleResize);
      
      this.destroySwipers();
      this.isDesktop = null;

      if (this.openedBy) {
        this.openedBy.focus();
        this.openedBy = null;
      }
    }

    pauseAllMedia() {
      for (const video of this.querySelectorAll('video')) {
        video.pause();
        video.currentTime = 0;
      }

      for (const slide of this.querySelectorAll('.swiper-slide')) {
        this.ejectDeferredVideo(slide);
      }
    }

    pauseSlideMedia(slideIndex) {
      if (!this.mainSwiper?.slides) return;

      const slide = this.mainSwiper.slides[slideIndex];
      if (!slide) return;

      for (const video of slide.querySelectorAll('video')) {
        video.pause();
      }

      this.ejectDeferredVideo(slide);
    }

    injectDeferredVideo(slide) {
      const template = slide.querySelector('template[data-deferred-video]');
      if (!template) return;

      const wrapper = template.parentElement;
      if (wrapper.querySelector('iframe')) return;

      wrapper.appendChild(template.content.cloneNode(true));
      const iframe = wrapper.querySelector('iframe');
      iframe?.addEventListener('load', () => {
        iframe.setAttribute('data-loaded', '');
      }, { once: true });
    }

    ejectDeferredVideo(slide) {
      if (!slide.querySelector('template[data-deferred-video]')) return;

      const wrapper = slide.querySelector('.product-zoom-modal__video-wrapper');
      wrapper?.querySelector('iframe')?.remove();
    }

    manageSlideMedia() {
      if (!this.mainSwiper?.slides) return;

      const activeIndex = this.mainSwiper.activeIndex;

      for (const [index, slide] of [...this.mainSwiper.slides].entries()) {
        const isActive = index === activeIndex;

        if (isActive) {
          this.injectDeferredVideo(slide);
        } else {
          this.ejectDeferredVideo(slide);
        }

        for (const video of slide.querySelectorAll('video')) {
          if (!isActive) {
            video.pause();
          }
        }
      }
    }

    initSwipers(initialIndex) {
      const initSwiper = () => {
        if (typeof Swiper === 'undefined') return;

        const thumbsContainer = this.querySelector('[data-zoom-thumbs]');
        const mainContainer = this.querySelector('[data-zoom-main]');
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;

        if (thumbsContainer) {
          this.thumbsSwiper = new Swiper(thumbsContainer, {
            direction: isDesktop ? 'vertical' : 'horizontal',
            slidesPerView: 'auto',
            spaceBetween: 10,
            freeMode: true,
            watchSlidesProgress: true,
            initialSlide: initialIndex,
            allowTouchMove: true,
            mousewheel: {
              forceToAxis: true
            }
          });
        }

        if (mainContainer) {
          this.mainSwiper = new Swiper(mainContainer, {
            slidesPerView: 1,
            spaceBetween: 0,
            initialSlide: initialIndex,
            on: {
              slideChange: (swiper) => {
                this.updateCounter();
                this.updateActiveThumbnail(swiper.activeIndex);
                this.manageSlideMedia();
                this.updateSlideFocusability();
              }
            }
          });
        }

        this.manageSlideMedia();
        this.updateSlideFocusability();
      };

      if (typeof Swiper !== 'undefined') {
        initSwiper();
      } else {
        document.addEventListener('swiperReady', initSwiper, { once: true });
      }
    }

    destroySwipers() {
      this.mainSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.mainSwiper = null;
      this.thumbsSwiper = null;
    }

    updateCounter() {
      if (!this.counterElement || !this.mainSwiper) return;
      
      const current = this.mainSwiper.activeIndex + 1;
      const total = this.mainSwiper.slides.length;
      this.counterElement.textContent = `${current} / ${total}`;
    }
  });
}

class ProductZoomOpener extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    event.preventDefault();
    
    const modalSelector = this.dataset.modal;
    const modal = document.querySelector(modalSelector);
    
    if (!modal) return;

    const slideIndex = Number.parseInt(this.dataset.slideIndex, 10) || 0;
    modal.openedBy = this;
    modal.show(slideIndex);
  }
}

if (!customElements.get('product-zoom-opener')) {
  customElements.define('product-zoom-opener', ProductZoomOpener);
}
